import { Fragment, useCallback, useMemo, useState } from 'react';
import { InView } from 'react-intersection-observer';

import {
  Checkbox,
  ListItemIcon,
  MenuItem,
  MenuList,
  styled,
} from '@material-ui/core';

import {
  FilterEditorComponentProps,
  SearchUtils,
  SimpleAutocomplete,
} from '@spotify/confidence-core-react';

import {
  useCurrentIdentity,
  useCurrentUserGroups,
  useIdentities,
} from '../../hooks';
import { IdentityItemMui as IdentityItem } from '../IdentityItem';

const Container = styled('div')({
  maxHeight: 300,
  overflowY: 'auto',
  // fixed width to prevent layout shift when as more items are loaded
  // that may cause the menu to expand
  maxWidth: 300,
  minWidth: 300,
});

const deduplicateItems = <T extends { value: string }>(
  items: (T | false | undefined | null)[],
): T[] => {
  const seen = new Set<string>();
  return items.filter((item): item is T => {
    if (!item) return false;
    if (seen.has(item.value)) return false;
    seen.add(item.value);
    return true;
  });
};

export const IdentityFilterEditor = ({
  value,
  onValueChange,
  onClose,
}: FilterEditorComponentProps<string[]>) => {
  const [query, setQuery] = useState('');

  const filter = SearchUtils.buildSearchFilter(query, 'displayName') ?? '';

  const { currentIdentity } = useCurrentIdentity();
  const { identities: groupIdentities } = useCurrentUserGroups();

  const { identities, hasNextPage, nextPage } = useIdentities({
    filter,
  });

  const allItems = useMemo(
    () =>
      deduplicateItems([
        currentIdentity && {
          value: currentIdentity.name,
          label: currentIdentity.displayName,
          identity: currentIdentity,
        },
        ...(groupIdentities?.map(identity => ({
          value: identity.name,
          label: identity.displayName,
          identity,
        })) ?? []),
        ...(identities ?? []).map(identity => ({
          value: identity.name,
          label: identity.displayName,
          identity,
        })),
      ]),
    [currentIdentity, groupIdentities, identities],
  );

  const handleCheckboxChange = useCallback(
    (itemValue: string, checked: boolean) => {
      if (checked) {
        onValueChange([...(value ?? []), itemValue]);
      } else {
        onValueChange(value?.filter(v => v !== itemValue) ?? []);
      }
    },
    [value, onValueChange],
  );

  return (
    <Container>
      <SimpleAutocomplete.Root
        multiple
        items={allItems}
        value={value ?? []}
        onValueChange={onValueChange}
        onInputChange={setQuery}
        onSelect={() => {
          onClose();
          return false;
        }}
      >
        {({
          items: autocompleteItems,
          getInputProps,
          getListboxProps,
          getOptionProps,
        }) => (
          <Fragment>
            <SimpleAutocomplete.Input
              data-track-source-anchor="1aee34e456" {...getInputProps()}
              placeholder="Search people or groups..."
              fullWidth
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              margin="dense"
            />
            <SimpleAutocomplete.Empty />

            <MenuList dense {...getListboxProps()}>
              {autocompleteItems.map((item, index) => (
                <MenuItem
                  disableGutters
                  key={item.value}
                  button
                  data-track-source-anchor="d8bba453a9;identity-filter-editor-menu-item;low" {...getOptionProps(index)}
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={value?.includes(item.value) ?? false}
                      onClick={e => {
                        e.stopPropagation();
                      }}
                      onChange={e =>
                        handleCheckboxChange(item.value, e.target.checked)
                      }
                     data-track-source-anchor="98ac21d850;identity-filter-editor-checkbox;low"/>
                  </ListItemIcon>
                  <IdentityItem identity={item.identity} />
                </MenuItem>
              ))}
              <InView
                onChange={(inView: boolean) => {
                  if (inView && hasNextPage) {
                    nextPage();
                  }
                }}
               data-track-source-anchor="807a5a6960;identity-filter-editor-in-view"/>
            </MenuList>
          </Fragment>
        )}
      </SimpleAutocomplete.Root>
    </Container>
  );
};
